import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Card,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Collapse,
} from "@mui/material";
import { useSelector } from "react-redux";
import { KeyboardArrowDown, Sort } from "@mui/icons-material";

function RebalanceOrderTable() {
  const { orders } = useSelector((state) => state.transaction);
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedTab, setSelectedTab] = useState('Pending');
  const [selectedStrategies, setSelectedStrategies] = useState(['All']);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);

  // Get unique strategy names for filter
  const strategies = useMemo(() => {
    const uniqueStrategies = new Set(orders
      .filter(order => order.action === 'REBALANCE')
      .map(order => order.strategy?.name)
      .filter(Boolean));
    return ['All', ...Array.from(uniqueStrategies)];
  }, [orders]);

  // Add order count calculations
  const orderCounts = useMemo(() => {
    const rebalanceOrders = orders.filter(order => order.action === 'REBALANCE');
    return {
      pending: rebalanceOrders.filter(order => order.status === 'PENDING').length,
      executed: rebalanceOrders.filter(order => 
        ['PARTIALLY_COMPLETED', 'COMPLETED'].includes(order.status)
      ).length,
      failedCanceled: rebalanceOrders.filter(order => 
        ['FAILED', 'CANCELLED'].includes(order.status)
      ).length
    };
  }, [orders]);

  // Filter orders based on selected tab and strategies
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      // First filter for REBALANCE orders only
      if (order.action !== 'REBALANCE') return false;

      // Status filter
      const statusMatch = (() => {
        switch (selectedTab) {
          case 'Pending':
            return order.status === 'PENDING';
          case 'Executed':
            return ['PARTIALLY_COMPLETED', 'COMPLETED'].includes(order.status);
          case 'Failed/Canceled':
            return ['FAILED', 'CANCELLED'].includes(order.status);
          default:
            return true;
        }
      })();

      // Strategy filter
      const strategyMatch = 
        selectedStrategies.includes('All') || 
        selectedStrategies.includes(order.strategy?.name);

      return statusMatch && strategyMatch;
    });
  }, [orders, selectedTab, selectedStrategies]);

  // Remove 'All' from the strategies array if it exists
  const filteredStrategies = strategies.filter(strategy => strategy !== 'All');

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Function to handle expanding/collapsing asset info
  const handleAssetInfoClick = (orderId) => {
    setExpandedRow(expandedRow === orderId ? null : orderId);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleStrategySelect = (strategy) => {
    if (selectedStrategies.includes(strategy)) {
      setSelectedStrategies(prev => prev.filter(s => s !== strategy));
    } else {
      setSelectedStrategies(prev => [...prev, strategy]);
    }
  };

  const renderTransactionDetails = (transactions) => {
    if (!transactions?.length) return null;

    return (
      <Box sx={{ 
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #1344EB',
        backgroundColor:'#f9f9f9',
        padding:'0 12px'
      }}>
        <Table size="small" sx={{ 
          '& .MuiTableCell-root': {
            borderBottom: '1px solid #E0E0E0',
            padding: '12px 16px',
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            color: '#0D0C3A',
            fontWeight: '500 !important',
            fontSize: '18px !important',
            padding: '12px 16px',
            fontFamily: 'inherit !important',
            fontStyle: 'inherit',
            lineHeight: '24px',
            letterSpacing: '0.15px',
            textAlign:'center',
           
          },
          '& .MuiTableBody-root .MuiTableCell-root': {
            backgroundColor: 'white',
            color: '#787A99',

            fontSize: '16px',
            backgroundColor:'#f9f9f9',
          },
          '& .MuiTableRow-root:last-child .MuiTableCell-root': {
            borderBottom: '1px solid #E0E0E0'
          },
          '& .MuiTableHead-root .MuiTableCell-root:first-child': {
            textAlign:'left'
          },
          '& .MuiTableBody-root .MuiTableCell-root:first-child': {
            textAlign:'left',
            color: '#43436A'
          },
          '& .MuiTableBody-root .MuiTableCell-root:not(:first-child)': {
            textAlign:'center'
          }
        }}>
          <TableHead>
            <TableRow>
              <TableCell>Asset</TableCell>
              <TableCell>Market Price</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Value ($)</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>{transaction.asset?.symbol || '-'}</TableCell>
                <TableCell>{transaction.price || '-'}</TableCell>
                <TableCell>{transaction.amount || '-'}</TableCell>
                <TableCell>{transaction.usdtEquivalent || '-'}</TableCell>
                <TableCell sx={{ textTransform: 'uppercase' }}>{transaction.status || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <>
      {/* Status Tabs and Strategy Filters Card */}
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        mb: 2
      }}>
        {/* Status Tabs */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 3,
          py: 1.5,
          borderBottom: '1px solid #E0E0E0'
        }}>
          <Box sx={{ display: 'flex', gap: 4 }}>
            {[
              { status: 'Pending', count: orderCounts.pending },
              { status: 'Executed', count: orderCounts.executed },
              { status: 'Failed/Canceled', count: orderCounts.failedCanceled }
            ].map(({ status, count }) => (
              <Box
                key={status}
                onClick={() => setSelectedTab(status)}
                sx={{
                  cursor: 'pointer',
                  color: selectedTab === status ? '#0066FF' : '#666666',
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    right: 0,
                    height: 2,
                    bgcolor: selectedTab === status ? '#0066FF' : 'transparent'
                  }
                }}
              >
                <Typography variant="body2">
                  {`${status} (${count})`}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button
            endIcon={<KeyboardArrowDown />}
            startIcon={<Sort />}
            sx={{ 
              textTransform: 'none',
              color: '#666666'
            }}
            onClick={handleSortClick}
          >
            Sort
          </Button>
        </Box>
        
        {/* Strategy Filters as Horizontal Tabs */}
        <Box sx={{ p: 2, overflowX: 'auto' }}>
          <Box sx={{ 
            display: 'flex',
            gap: 1,
            '&::-webkit-scrollbar': {
              height: '4px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888'
            }
          }}>
            {['All', ...filteredStrategies].map((strategy) => (
              <Button
                key={strategy}
                onClick={() => handleStrategySelect(strategy)}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  border: '1px solid #E0E0E0',
                  borderRadius: 1,
                  color: selectedStrategies.includes(strategy) ? '#0066FF' : '#666666',
                  bgcolor: selectedStrategies.includes(strategy) ? '#F0F7FF' : 'transparent',
                  textTransform: 'none',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: selectedStrategies.includes(strategy) ? '#F0F7FF' : 'transparent',
                    border: '1px solid #E0E0E0',
                  }
                }}
              >
                {strategy}
              </Button>
            ))}
          </Box>
        </Box>
      </Card>

      {/* Main Table Card */}
      <Card sx={{ 
        borderRadius: '8px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        backgroundColor: '#FFFFFF',
      }}>
        <TableContainer sx={{ 
          overflow: 'visible' 
        }}>
          <Table sx={{
            '& .MuiTableCell-root': {
              borderBottom: 'none',
              width: '14.28%' 
            },
            '& .MuiTableRow-root': {
              borderBottom: 'none'
            }
          }}>
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ color: '#666666'}}>Investor Email</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Order Type</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Strategy Name</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Amount ($)</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Status</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Date of Creation</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <TableRow>
                    <TableCell align="left">{order.userRole?.user?.email || '-'}</TableCell>
                    <TableCell align="center">{order.type || '-'}</TableCell>
                    <TableCell align="center">{order.strategy?.name || '-'}</TableCell>
                    <TableCell align="center">{order.amount ? order.amount.toFixed(2) : '-'}</TableCell>
                    <TableCell align="center">{order.status || '-'}</TableCell>
                    <TableCell align="center">{order.createdAt ? new Date(order.createdAt).toLocaleString() : '-'}</TableCell>
                    <TableCell align="right">
                      {order.transactions?.length > 0 ? (
                        <Button
                          sx={{
                            textTransform: 'none',
                            color: '#2196F3',
                            fontWeight: 'bold',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              textDecoration: 'underline'
                            }
                          }}
                          onClick={() => handleAssetInfoClick(order.id)}
                        >
                          {expandedRow === order.id ? 'Hide Asset Info' : 'See Asset Info'}
                        </Button>
                      ) : (
                        <Button
                          disabled
                          sx={{
                            textTransform: 'none',
                            color: '#666666',
                            opacity: 0.5,
                            cursor: 'not-allowed'
                          }}
                        >
                          See Asset Info
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {order.transactions?.length > 0 && (
                    <TableRow>
                      <TableCell 
                        colSpan={7} 
                        sx={{ 
                          py: 0,
                          borderBottom: expandedRow === order.id ? '1px solid #E0E0E0' : 'none',
                          
                        }}
                      >
                        <Collapse in={expandedRow === order.id} timeout="auto" unmountOnExit >
                          <Box sx={{ 
                            py: 2,
                            px: 1,
                            overflow: 'visible',
                            backgroundColor: '#ffffff'
                          }}>
                            {renderTransactionDetails(order.transactions)} 
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </>
  );
}

export default RebalanceOrderTable; 